import { Badge } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Loader2 } from 'lucide-react';
import WorkspaceCardFrame from './WorkspaceCardFrame.jsx';
import WorkspaceInitialsAvatar from './WorkspaceInitialsAvatar.jsx';
import StudioChips from './StudioChips.jsx';
import { studioLabelKey } from '../workspaceSelectorData.js';

/**
 * The active workspace, rendered as the hero of the selector: bigger padding,
 * a bigger avatar and name, a brand-tinted surface and a green "Active
 * workspace" badge. Deliberately taller than the compact cards so the
 * "where I am now" workspace reads first (see the prototype).
 */
function WorkspaceHeroCard({ workspace, meta, disambiguator, switching = false, disabled = false, onSelect, onOpenStudio }) {
  const { t } = useTranslation('core-shell');

  return (
    <WorkspaceCardFrame
      label={t('workspaceSelector.openWorkspace', {
        name: disambiguator ? `${workspace.name} (${disambiguator})` : workspace.name,
      })}
      active
      busy={switching}
      disabled={disabled}
      // Selecting the card body lands on the workspace's DEFAULT studio (its
      // first accessible one, CT-4940) rather than the bare dashboard.
      onSelect={() => onSelect(workspace.id, meta.defaultStudioId)}
      className="p-6"
    >
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center">
        {/* Identity */}
        <div className="flex min-w-0 items-start gap-4 lg:w-72 lg:flex-shrink-0">
          <WorkspaceInitialsAvatar
            src={workspace.avatarSrc}
            variant="hero"
            initials={workspace.initials}
            name={workspace.name}
            accentClassName={meta.avatarClassName}
          />
          <div className="min-w-0">
            <p className="truncate text-base font-semibold text-content">{workspace.name}</p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Badge variant={meta.industryVariant} size="small">
                {meta.industry}
              </Badge>
              <Badge variant={meta.roleVariant} size="small">
                {meta.role}
              </Badge>
            </div>
            <div className="mt-2">
              <Badge variant="success" size="small">
                {t('workspaceSelector.activeWorkspace')}
              </Badge>
            </div>
          </div>
        </div>

        {/* Studios */}
        <div className="pointer-events-auto min-w-0 flex-1">
          <StudioChips
            studios={meta.studios}
            disabled={switching || disabled}
            onOpenStudio={(studioId) => onOpenStudio(workspace.id, studioId)}
          />
        </div>

        {/* Last visited */}
        <div className="flex items-center gap-2 lg:w-44 lg:flex-shrink-0 lg:justify-end">
          {switching ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-content-muted">
              <Loader2 size={14} className="animate-spin" />
              {t('workspaceSelector.switching')}
            </span>
          ) : (
            <span className="text-right text-xs text-content-faint">
              {t('workspaceSelector.lastIn', {
                studio: t(studioLabelKey(meta.lastVisitedStudioId)),
                time: meta.lastVisitedLabel,
              })}
            </span>
          )}
        </div>
      </div>
    </WorkspaceCardFrame>
  );
}

export default WorkspaceHeroCard;
